import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import ReactMapboxGL from 'react-mapbox-gl';
import ReactMapsGL, {
  Timeline,
  WeatherLayer,
  DataInspector,
  LegendControl,
  
} from '@aerisweather/react-mapsgl';
// import {AerisMapController} from '@aerisweather/mapsgl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@aerisweather/mapsgl/dist/mapsgl.css';
import GradientLegend from './GradientLegend';

import { useReduxStore } from '../hooks/useReduxStore';
import { forecastService } from '../services/forecastService';
import useIsFreePlan from '../hooks/useIsFreePlan';
// import useCurrentSubscription from '../hooks/useCurrentSubscription';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { getLayerFeatureName } from '../constants/lockedFeatures';
// import { getPlanName } from '../utils/subscriptionUtils';
import { LockClosedIcon } from '@heroicons/react/24/solid';

// Mapbox setup
const Map = ReactMapboxGL({
  accessToken:
    'pk.eyJ1IjoiYm9jb2xlODIiLCJhIjoiY21mNDFjd2lyMDBxNDJqc2RvN3BkcDl0dSJ9.VyLeKZTBGiZOGc9NC9m3Ag',
});

// MapsGL setup
const MapController = ReactMapsGL({
  accessKeys: {
    id: 'bg0TphJbIuEYf3MndFXXc',
    secret: 'A4cm5jU6LgrueWPcxjg7crJY1mrt77ECV67dJGt8',
  },
});





// Default fallback: Minneapolis, MN (~44.9778° N, 93.2650° W).
const DEFAULT_MAP_CENTER = { lat: 44.9778, lon: -93.265 };

const MapView = ({ center = DEFAULT_MAP_CENTER, zoom = 7 }) => {
  // const currentSubscription = useCurrentSubscription();
  // const planName = getPlanName(currentSubscription);
  // const isFreePlan = useIsFreePlan();
  // const { handleLockedClick } = useUpgradeModal();

  const { getState } = useReduxStore();
  const { dashBoardData } = getState('Home') || {};
  const currentSubscription = dashBoardData?.subscription;
  const planName = (currentSubscription?.planId?.name || 'Free').toLowerCase();
  const isFreePlan = useIsFreePlan();
  const { handleLockedClick } = useUpgradeModal();
  const [map, setMap] = useState('');
  const mapboxMapRef = useRef(null);
  const mapControllerRef = useRef();
  const mapFrameRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState('temperatures');
  const [selectedChildLayer, setSelectedChildLayer] = useState('feels-like');
  const [showMobileLayers, setShowMobileLayers] = useState(false); // State for mobile layer popup
  const requestIdRef = useRef(0);
  const [locationDetails, setLocationDetails] = useState({
    placeName: 'Minneapolis, MN',
    lat: center.lat,
    lon: center.lon,
    current: null,
    loading: false,
    error: null,
  });

  // Map load handler
  const onMapLoad = (mapEl) => {
    if (isUnmountingRef.current) return;
    
    try {
      setMap(mapEl);
      mapboxMapRef.current = mapEl;
    } catch (error) {
      console.warn('Error in onMapLoad:', error);
    }
  };

  const fetchLocationWeather = useCallback(async (lat, lon) => {
    const requestId = ++requestIdRef.current;
    setLocationDetails((prev) => ({
      ...prev,
      lat,
      lon,
      loading: true,
      error: null,
    }));

    try {
      const response = await forecastService.getCurrentWeather(`${lat},${lon}`);
      const res0 = response?.data?.response?.[0];
      const period = res0?.periods?.[0];
      const place = res0?.place;

      if (!period) {
        throw new Error('No weather data available for this location');
      }

      const placeName = [place?.name, place?.state, place?.country]
        .filter(Boolean)
        .join(', ') || `Lat ${lat.toFixed(4)}, Lon ${lon.toFixed(4)}`;

      if (requestId !== requestIdRef.current) return;

      setLocationDetails({
        placeName,
        lat,
        lon,
        current: {
          tempC: period.tempC,
          tempF: period.tempF,
          feelslikeC: period.feelslikeC,
          humidity: period.humidity,
          pressureMB: period.pressureMB,
          windSpeedMPH: period.windSpeedMPH,
          visibilityKM: period.visibilityKM,
          pop: period.pop,
          precipMM: period.precipMM,
          snowCM: period.snowCM,
          weather: period.weather || period.weatherPrimary || 'Clear sky',
          timestamp: period.timestamp,
        },
        loading: false,
        error: null,
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setLocationDetails((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || 'Failed to fetch location weather',
      }));
    }
  }, []);

  // Layer visibility state - only one layer active at a time
  const [layerVisibility, setLayerVisibility] = useState({
    temperature: true,
    radar: false,
    satellite: false,
    advisory: false,
    precipitation: false,
    wind: false,
    visibility: false,
    pressure: false,
    snowfall: false,
    forecast: false,
    roadCondition: false,
    observation: false
  });

  const allowedLayers = useMemo(() => {
    if (planName === 'free') return new Set(['temperature']);
    if (planName === 'starter') return new Set(['temperature', 'wind', 'radar', 'precipitation', 'satellite']);
    if (planName === 'pro' || planName === 'custom') return new Set(Object.keys(layerVisibility));
    return new Set(['temperature']);
  }, [currentSubscription]);

  const isLocked = (layerName) => !allowedLayers.has(layerName);

  useEffect(() => {
    // When plan changes, turn off disallowed layers and ensure at least one allowed is active
    setLayerVisibility((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (!allowedLayers.has(k)) next[k] = false;
      });
      const anyAllowedActive = Object.keys(next).some((k) => allowedLayers.has(k) && next[k]);
      if (!anyAllowedActive && allowedLayers.has('temperature')) {
        next.temperature = true;
      }
      return next;
    });
  }, [allowedLayers]);

  // Handle layer visibility changes - allow toggling on/off
  const handleLayerToggle = (layerName) => {
    if (isLocked(layerName)) {
      if (isFreePlan) {
        handleLockedClick(getLayerFeatureName(layerName));
      }
      return;
    }
    
    // mapControllerRef.current.source.add("feels-like")
    // if (isUnmountingRef.current) return;
    // if (layerName === 'advisory') return; // Disable advisory

    // If the layer is already active, turn it off
    if (layerVisibility[layerName]) {
      const newVisibility = {
        ...layerVisibility,
        [layerName]: false
      };
      setLayerVisibility(newVisibility);
      return;
    }

    // Turn off all layers and turn on the selected one
    const newVisibility = Object.keys(layerVisibility).reduce((acc, key) => {
      acc[key] = key === layerName;
      return acc;
    }, {});
    setLayerVisibility(newVisibility);
  };

  // Get active legend based on visible layers
  const getActiveLegend = () => {
    if (isUnmountingRef.current) return null;

    if (layerVisibility.temperature) {
      return {
        title: 'Temperature',
        description: 'Shows current temperature conditions. Blue indicates cold temperatures, red indicates hot temperatures.',
        colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
        minLabel: '20°F',
        maxLabel: '80°F',
        minValue: 20,
        maxValue: 80,
        unit: '°F',
        icon: '🌡️',
        dataSource: 'AerisWeather Temperature Data'
      };
    }

    if (layerVisibility.radar) {
      return {
        title: 'Radar',
        description: 'Shows precipitation intensity.Blue indicates very light rain, Green indicates light rain,yellow indicates medium rain, red indicates heavy rain.',
        colors: ["#0080ee","#1c6b0b", '#ffff00',  '#ff0000', ],
        minLabel: '1 mm/h',
        maxLabel: '50+ mm/h',
        minValue: 1,
        maxValue: 50,
        unit: 'mm/h',
        icon: '📡',
        dataSource: 'AerisWeather Radar Data'
      };
    }

    if (layerVisibility.satellite) {
      return {
        title: 'Satellite',
        description: 'Shows cloud cover and atmospheric conditions. Dark indicates clear sky, white indicates dense clouds.',
        colors: ['#000000', '#404040', '#808080', '#c0c0c0', '#ffffff'],
        minLabel: 'Clear Sky',
        maxLabel: 'Dense Clouds',
        icon: '🛰️',
        dataSource: 'AerisWeather Satellite Data'
      };
    }

    if (layerVisibility.precipitation) {
      return {
        title: 'Precipitation',
        description: 'Precipitation: Light (light green), Medium (green), Heavy (dark green).',
        colors: ['#86efac', '#16a34a', '#14532d'],
        minLabel: '5 mm',
        maxLabel: '70+ mm',
        minValue: 5,
        maxValue: 70,
        unit: 'mm',
        icon: '🌧️',
        dataSource: 'AerisWeather Precipitation Data'
      };
    }

    if (layerVisibility.wind) {
      return {
        title: 'Wind',
        description: 'Shows wind speed and direction. Blue indicates calm winds, red indicates strong winds.',
        colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
        minLabel: '5 mph',
        maxLabel: '20+ mph',
        minValue: 5,
        maxValue: 20,
        unit: 'mph',
        icon: '💨',
        dataSource: 'AerisWeather Wind Data'
      };
    }

    if (layerVisibility.visibility) {
      return {
        title: 'Visibility',
       description: 'Shows visibility conditions. Ligh Yellow indicates clear visibility, Dark Yellow indicates poor visibility.',
        colors: ['#ffb700', '#fee904', '#f8f8ca', ],
        minLabel: '1 km',
        maxLabel: '10+ km',
        minValue: 1,
        maxValue: 10,
        unit: 'km',
        icon: '👁️',
        dataSource: 'AerisWeather Visibility Data'
      };
    }

    if (layerVisibility.pressure) {
      return {
        title: 'Barometric Pressure',
        description: 'Shows atmospheric pressure. Red indicates low pressure, blue indicates high pressure.',
        colors: ['#ff0000', '#ff8000', '#ffff00', '#00ff00', '#00ffff', '#0000ff'],
        minLabel: '950 mb',
        maxLabel: '1050 mb',
        minValue: 950,
        maxValue: 1050,
        unit: 'mb',
        icon: '📊',
        dataSource: 'AerisWeather Pressure Data'
      };
    }

    if (layerVisibility.snowfall) {
      return {
        title: 'Snow Depth',
        description: 'Snow depth: Light (light blue), Moderate (dark blue), Heavy (pink).',
        colors: ["#71c6f5", '#1d4ed8', '#ec4899',"#fc7cbb"],
        minLabel: '5 in',
        maxLabel: '30+ in',
        minValue: 5,
        maxValue: 30,
        unit: 'in',
        icon: '❄️',
        dataSource: 'AerisWeather Snowfall Data'
      };
    }

    if (layerVisibility.roadCondition) {
      return {
        title: 'Road Conditions',
        description: 'Depicts a summary of road conditions based on severity, where green indicates dry roads with no issues, yellow indicates the potential for hazardous road conditions (wet, slick), and red indicates a high risk of adverse road conditions.',
        type: 'discrete',
        items: [
          { color: '#22c55e', label: 'Dry / No Issues' },
          { color: '#facc15', label: 'Potentially Hazardous (Wet, Slick)' },
          { color: '#ef4444', label: 'High Risk / Adverse' }
        ],
        icon: '🛣️',
        dataSource: 'AerisWeather Road Weather Summary'
      };
    }

    if (layerVisibility.forecast) {
      return {
        title: 'Forecast Points',
        description: 'Shows weather forecast data points. Orange indicates current conditions, purple indicates future predictions.',
        colors: ['#ff8000', '#ffcc00', '#ffff00', '#cc66ff', '#8000ff'],
        minLabel: 'Current',
        maxLabel: 'Future',
        icon: '🔮',
        dataSource: 'AerisWeather Forecast Data'
      };
    }

    if (layerVisibility.observation) {
      return {
        title: 'Observation',
        description: 'Combined observation overlays including feels-like temperature, visibility, and wind.',
        type: 'composite',
        legends: [
          {
            title: 'Feels-like',
            colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
            minLabel: '20°F',
            maxLabel: '80°F',
            minValue: 20,
            maxValue: 80,
            unit: '°F'
          },
          {
            title: 'Visibility',
 description: 'Shows visibility conditions. Ligh Yellow indicates clear visibility, Dark Yellow indicates poor visibility.',
        colors: ['#ffb700', '#fee904', '#f8f8ca', ],
        minLabel: '1 km',
        maxLabel: '10+ km',
        minValue: 1,
        maxValue: 10,
            unit: 'km'
          },
          {
            title: 'Wind',
            colors: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff8000', '#ff0000'],
            minLabel: '5 mph',
            maxLabel: '20+ mph',
            minValue: 5,
            maxValue: 20,
            unit: 'mph'
          }
        ],
        icon: '🌤️',
        dataSource: 'AerisWeather Observations'
      };
    }
    
    // Fallback legend when no layers are active
    return {
      title: 'Weather Map',
      description: 'Select a weather layer to view detailed information and legend.',
      colors: ['#404040', '#606060', '#808080', '#a0a0a0', '#c0c0c0'],
      minLabel: 'No Data',
      maxLabel: 'No Data',
      icon: '🌤️',
      dataSource: 'AerisWeather'
    };
  };

  // Enhanced cleanup with error handling and unmounting protection
  // useEffect(() => {
  //   return () => {
  //     isUnmountingRef.current = true;
      
  //     // Suppress MapsGL errors during unmounting
  //     const originalConsoleError = console.error;
  //     const originalConsoleWarn = console.warn;
      
  //     console.error = (...args) => {
  //       if (args[0] && typeof args[0] === 'string' && 
  //           (args[0].includes('mapsgl') || args[0].includes('trigger') || args[0].includes('WebGL'))) {
  //         return; // Suppress MapsGL-related errors during unmounting
  //       }
  //       originalConsoleError.apply(console, args);
  //     };
      
  //     console.warn = (...args) => {
  //       if (args[0] && typeof args[0] === 'string' && 
  //           (args[0].includes('mapsgl') || args[0].includes('trigger') || args[0].includes('WebGL'))) {
  //         return; // Suppress MapsGL-related warnings during unmounting
  //       }
  //       originalConsoleWarn.apply(console, args);
  //     };

  //     try {
  //       // Clean up MapController first
  //       if (mapControllerRef.current && typeof mapControllerRef.current.destroy === "function") {
  //         mapControllerRef.current.destroy();
  //         mapControllerRef.current = null;
  //       }
  //     } catch (error) {
  //       console.warn('Error destroying MapController:', error);
  //     }

  //     try {
  //       // Clean up Mapbox map
  //       if (mapboxMapRef.current && typeof mapboxMapRef.current.remove === "function") {
  //         mapboxMapRef.current.remove();
  //         mapboxMapRef.current = null;
  //       }
  //     } catch (error) {
  //       console.warn('Error removing Mapbox map:', error);
  //     }

  //     // Restore console methods
  //     setTimeout(() => {
  //       console.error = originalConsoleError;
  //       console.warn = originalConsoleWarn;
  //     }, 100);
  //   };
  // }, []);
  
useEffect(()=>{
  // if (mapboxMapRef.current) {
  //   const canvas = mapboxMapRef.current.getCanvas();
  //   const clone = canvas.cloneNode(true);
  //   canvas.parentNode.replaceChild(clone, canvas); // saare listeners wipe ho jate hain
  //   mapboxMapRef.current.remove();
  //   mapboxMapRef.current = null;
  // }
// return()=>{
//     setLayerVisibility({
//     temperature: false,
//     radar: false,
//     satellite: false,
//     advisory: false,
//     precipitation: false,
//     wind: false,
//     visibility: false,
//     pressure: false,
//     snowfall: false,
//     forecast: false
//   })
//   mapboxMapRef?.current?.remove()
//   mapboxMapRef?.current?.off()
//   mapboxMapRef?.current?.removeLayer("feels-like")
//   // const canvas = document.querySelector(".mapboxgl-canvas");
//   // if (canvas && canvas.parentNode) {
//   //   canvas.parentNode.removeChild(canvas);
//   // }
//   // mapsgl?.AerisMapController?.remove()

// }

},[])

  useEffect(() => {
    fetchLocationWeather(center.lat, center.lon);
  }, [center.lat, center.lon, fetchLocationWeather]);

  const layerRailItems = [
    { key: 'radar', label: 'Radar', icon: '📡' },
    { key: 'satellite', label: 'Satellite', icon: '🛰️' },
    { key: 'temperature', label: 'Temperatures', icon: '🌡️' },
    { key: 'wind', label: 'Wind Speeds', icon: '💨' },
    { key: 'visibility', label: 'Visibility', icon: '👁️' },
    { key: 'precipitation', label: 'Accum Precip', icon: '🌧️' },
    { key: 'snowfall', label: 'Snow Depth', icon: '❄️' },
    { key: 'pressure', label: 'Pressure', icon: '📊' },
    { key: 'roadCondition', label: 'Road Condition', icon: '🛣️' },
    { key: 'observation', label: 'Observations', icon: '🔮' }
  ];

  const activeLegend = getActiveLegend();
  const lastUpdated = new Date((locationDetails?.current?.timestamp || Date.now() / 1000) * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const activeLayerKey = Object.keys(layerVisibility).find((key) => layerVisibility[key]);
  const activeLayerValue = useMemo(() => {
    const current = locationDetails?.current;
    if (!current || !activeLayerKey) return 'No active layer selected';

    switch (activeLayerKey) {
      case 'temperature':
        return `${Math.round(current.tempC ?? 0)}°C (${Math.round(current.tempF ?? 0)}°F)`;
      case 'observation':
        return `${Math.round(current.feelslikeC ?? current.tempC ?? 0)}°C feels like`;
      case 'wind':
        return `${Math.round(current.windSpeedMPH ?? 0)} mph`;
      case 'visibility':
        return `${(current.visibilityKM ?? 0).toFixed(1)} km`;
      case 'pressure':
        return `${Math.round(current.pressureMB ?? 0)} mb`;
      case 'precipitation':
      case 'radar':
        return `${Math.round(current.precipMM ?? 0)} mm precip (${Math.round(current.pop ?? 0)}%)`;
      case 'snowfall':
        return `${Math.round(current.snowCM ?? 0)} cm snow`;
      case 'satellite':
        return current.weather || 'Cloud/sky conditions';
      case 'roadCondition':
        return 'Derived from precip, temp and visibility trends';
      default:
        return 'Layer data available';
    }
  }, [activeLayerKey, locationDetails]);

  const handleZoomIn = () => {
    mapboxMapRef.current?.zoomIn?.();
  };

  const handleZoomOut = () => {
    mapboxMapRef.current?.zoomOut?.();
  };

  const handleFullscreen = async () => {
    if (!mapFrameRef.current) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen?.();
      return;
    }
    await mapFrameRef.current.requestFullscreen?.();
  };

  const handleMapClick = (mapInstance, event) => {
    const sourceEvent = event?.lngLat ? event : mapInstance;
    const lat = sourceEvent?.lngLat?.lat;
    const lon = sourceEvent?.lngLat?.lng;
    if (typeof lat !== 'number' || typeof lon !== 'number') return;
    fetchLocationWeather(lat, lon);
  };

  const layerRailButtons = layerRailItems.map((layer) => {
    const locked = isLocked(layer.key);
    const active = !!layerVisibility[layer.key];
    return (
      <button
        key={layer.key}
        type="button"
        onClick={() => handleLayerToggle(layer.key)}
        className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition ${
          active
            ? 'bg-blue-500/30 border-blue-300 text-white'
            : 'bg-[#2a3655]/70 border-gray-600 text-gray-100'
        } ${locked ? 'opacity-80 hover:bg-white/5' : 'hover:bg-[#3a4b73]'}`}
        title={locked ? `Unlock ${getLayerFeatureName(layer.key)}` : layer.label}
      >
        <span className="text-sm">{layer.label}</span>
        {locked ? (
          <LockClosedIcon className="h-5 w-5 text-amber-300/90" aria-hidden />
        ) : (
          <span className="text-base">{layer.icon}</span>
        )}
      </button>
    );
  });

  const detailsPanelContent = (
    <>
      <div className="px-4 py-3 border-b border-gray-300">
        <p className="text-sm text-[#20293F] font-semibold">Local Weather</p>
        <h3 className="text-3xl text-[#1f2937] leading-tight mt-1">{locationDetails.placeName}</h3>
        <p className="text-xs text-gray-600 mt-1">{locationDetails.lat.toFixed(5)}, {locationDetails.lon.toFixed(5)}</p>
        <p className="text-[11px] text-blue-700 mt-1">Click anywhere on the map to refresh this panel.</p>
      </div>

      <div className="px-4 py-3 border-b border-gray-300">
        <h4 className="text-[#1f2937] font-semibold">Active Alerts</h4>
        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          High Wind Warning
        </div>
        <p className="mt-2 text-xs text-gray-500">Powered by alerts endpoint</p>
      </div>

      <div className="px-4 py-3 border-b border-gray-300">
        <h4 className="text-[#1f2937] font-semibold">Latest Conditions</h4>
        <p className="text-xs text-gray-500 mt-1">Last updated at {lastUpdated}</p>
        <div className="mt-2 flex items-end justify-between">
          <p className="text-5xl font-semibold text-[#1f2937]">{Math.round(locationDetails?.current?.tempC ?? 0)}°C</p>
          <p className="text-sm text-gray-700">{locationDetails?.current?.weather || activeLegend?.title || 'Weather'}</p>
        </div>
        {locationDetails.loading && <p className="text-xs text-blue-700 mt-2">Loading location details...</p>}
        {locationDetails.error && <p className="text-xs text-red-600 mt-2">{locationDetails.error}</p>}
      </div>

      <div className="px-4 py-3 border-b border-gray-300">
        <h4 className="text-[#1f2937] font-semibold">Impacts</h4>
        <div className="mt-3 space-y-2 text-xs">
          <div>
            <div className="flex justify-between mb-1"><span>Temperature Risk</span><span>Low</span></div>
            <div className="h-2 bg-gray-200 rounded-full"><div className="h-2 rounded-full bg-lime-500 w-1/5"></div></div>
          </div>
          <div>
            <div className="flex justify-between mb-1"><span>Severe Storms</span><span>Low</span></div>
            <div className="h-2 bg-gray-200 rounded-full"><div className="h-2 rounded-full bg-lime-500 w-1/5"></div></div>
          </div>
        </div>
        <p className="text-xs text-gray-600 mt-3">
          <span className="font-semibold">Active layer:</span> {activeLegend?.title} - {activeLayerValue}
        </p>
      </div>

      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <span>{activeLegend?.icon || '🌤️'}</span>
          <h4 className="text-[#1f2937] font-semibold">{activeLegend?.title || 'Weather Map'}</h4>
        </div>
        {activeLegend?.type === 'discrete' && Array.isArray(activeLegend?.items) ? (
          <div className="space-y-2">
            {activeLegend.items.map((item) => (
              <div key={item.label} className="flex items-center gap-2 text-xs text-gray-700">
                <span className="w-3 h-3 rounded-full" style={{ background: item.color }}></span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        ) : (
          <GradientLegend
            height="h-3"
            colors={activeLegend?.colors || ['#404040', '#808080', '#c0c0c0']}
            minLabel={activeLegend?.minLabel}
            maxLabel={activeLegend?.maxLabel}
            minValue={activeLegend?.minValue}
            maxValue={activeLegend?.maxValue}
            unit={activeLegend?.unit}
          />
        )}
        <p className="text-xs text-gray-600 mt-2">{activeLegend?.description}</p>
      </div>
    </>
  );

  return (
    <div className="w-full">
      <div className="md:hidden flex items-center justify-between bg-[#20293F] p-4 rounded-lg border border-gray-500 cursor-pointer hover:bg-[#2a3655] transition-colors mb-3" onClick={() => setShowMobileLayers((prev) => !prev)}>
        <span className="text-white font-semibold">Map Layers</span>
        <span className="text-white text-xl">{showMobileLayers ? '−' : '⚙️'}</span>
      </div>

      {showMobileLayers && (
        <div className="md:hidden mb-3 rounded-xl border border-gray-500 bg-[#1c2538] p-2">
          <div className="space-y-2 max-h-[34vh] overflow-y-auto">
            {layerRailButtons}
          </div>
        </div>
      )}

      <div ref={mapFrameRef} className="relative w-full h-[62vh] md:h-[80vh] rounded-2xl border border-gray-700 overflow-hidden bg-[#0f172a]">
        <Map
          center={[center.lon, center.lat]}
          zoom={[zoom]}
          onStyleLoad={onMapLoad}
          onClick={handleMapClick}
          style="mapbox://styles/mapbox/light-v9"
          containerStyle={{ width: '100%', height: '100%' }}
        >
          {map && (
            <MapController
              onLoadComplete={(controller) => {
                mapControllerRef.current = controller;
              }}
              onError={(error) => {
              }}
              strategy="mapbox"
              map={map}
            >
              <Timeline
                start={new Date(Date.now() - 3600 * 12 * 1000)}
                duration={6}
                isPlaying={playing}
              />
              <DataInspector event="move" />
              <LegendControl width={0} toggleOnClick={false} />

              {layerVisibility.observation && <WeatherLayer key="humidity" id="humidity" />}
              {layerVisibility.observation && <WeatherLayer key="observation" id="feels-like" />}
              {layerVisibility.temperature && <WeatherLayer key="feels-like" id="feels-like" />}
              {layerVisibility.radar && <WeatherLayer key="radar" id="radar" />}
              {layerVisibility.satellite && <WeatherLayer key="satellite" id="satellite" />}
              {layerVisibility.precipitation && <WeatherLayer key="precip-accum" id="precip-accum" />}
              {(layerVisibility.wind || layerVisibility.observation) && <WeatherLayer key="wind-particles" id="wind-particles" />}
              {(layerVisibility.visibility || layerVisibility.observation) && <WeatherLayer key="visibility" id="visibility" />}
              {layerVisibility.pressure && <WeatherLayer key="pressure-msl-contour" id="pressure-msl-contour" />}
              {layerVisibility.snowfall && <WeatherLayer key="snow-depth" id="snow-depth" />}
              {layerVisibility.roadCondition && <WeatherLayer key="road-weather-summary-us" id="road-weather-summary-us" />}

              <WeatherLayer id="place-city" />
              <WeatherLayer id="admin-3-4-boundaries" />
              <WeatherLayer id="admin-2-boundaries" />
              {layerVisibility.temperature && <WeatherLayer key="temperatures-text" id="temperatures-text" />}
              {layerVisibility.observation && <WeatherLayer key="observation-text" id="temperatures-text" />}
            </MapController>
          )}
        </Map>

        <aside className="hidden md:block absolute left-3 top-3 z-20 w-[330px] max-w-[calc(100%-1.5rem)] max-h-[calc(100%-7rem)] overflow-y-auto rounded-xl border border-gray-300/40 bg-white/95 shadow-xl">
          {detailsPanelContent}
        </aside>

        <div className="hidden md:flex md:absolute md:inset-auto md:right-3 md:top-3 md:z-20 md:w-[220px] md:h-[78%]">
          <div className="w-full rounded-xl border border-gray-400/70 bg-[#1c2538]/90 backdrop-blur-sm p-2 overflow-y-auto">
            <div className="space-y-2">{layerRailButtons}</div>
          </div>
        </div>

        <div className="absolute left-3 bottom-3 z-20 rounded-xl border border-gray-500 bg-[#0f172a]/90 backdrop-blur-sm p-1">
          <div className="flex items-center gap-1">
            <button onClick={handleZoomIn} className="w-10 h-10 rounded-lg bg-[#1e293b] text-white text-xl border border-gray-500 hover:bg-[#334155]">+</button>
            <button onClick={handleZoomOut} className="w-10 h-10 rounded-lg bg-[#1e293b] text-white text-xl border border-gray-500 hover:bg-[#334155]">-</button>
            <button onClick={handleFullscreen} className="w-10 h-10 rounded-lg bg-[#1e293b] text-white border border-gray-500 hover:bg-[#334155] flex items-center justify-center" aria-label="Toggle fullscreen">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M8 3H3v5" />
                <path d="M16 3h5v5" />
                <path d="M21 16v5h-5" />
                <path d="M8 21H3v-5" />
              </svg>
            </button>
          </div>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 bottom-3 z-20 w-[min(90%,520px)] rounded-2xl border border-gray-500 bg-[#0f172a]/90 backdrop-blur-sm px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPlaying((p) => !p)}
              className={`w-11 h-11 rounded-full flex items-center justify-center ${playing ? 'bg-red-600' : 'bg-blue-600'} text-white`}
            >
              {playing ? '❚❚' : '▶'}
            </button>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">{new Date().toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
              <input type="range" min="0" max="100" defaultValue="55" className="w-full mt-2 accent-cyan-400" />
            </div>
          </div>
        </div>
      </div>

      <aside className="md:hidden mt-4 rounded-xl border border-gray-300/70 bg-white shadow">
        {detailsPanelContent}
      </aside>
    </div>
  );
};

export default MapView;



